import {
  ITemplateConfigurationDto,
  ITemplateConfigurationFormValue,
  ITemplateModificationDto,
  TContactBlockDto,
  TContactBlockModel,
  THeaderDto,
  THeaderModel,
} from './template.interface';

export class TemplatesEditorMapper {
  /**
   * Escapes special characters for API transmission:
   * - $ -> \$ (only if not followed by {)
   * - @ -> \@ (always)
   */
  private static escapeSpecialChars(value: string): string {
    if (!value) return value;
    return value
      .replace(/\$(?!\{)/g, '\\$')
      .replace(/@/g, '\\@');
  }

  /**
   * Unescapes special characters for display in the editor:
   * - \$ -> $
   * - \@ -> @
   */
  private static unescapeSpecialChars(value: string): string {
    if (!value) return value;
    return value
      .replace(/\\\$/g, '$')
      .replace(/\\@/g, '@');
  }

  static toModel(dto: ITemplateConfigurationDto): ITemplateConfigurationFormValue {
    return {
      ...dto,
      contact_note: this.unescapeSpecialChars(dto.contact_note || ''),
      letterbox_sender: dto.letterbox_sender ? {
        ...dto.letterbox_sender,
        name: this.unescapeSpecialChars(dto.letterbox_sender.name),
        street: this.unescapeSpecialChars(dto.letterbox_sender.street),
        street_number: this.unescapeSpecialChars(dto.letterbox_sender.street_number),
        postal_code: this.unescapeSpecialChars(dto.letterbox_sender.postal_code),
        city: this.unescapeSpecialChars(dto.letterbox_sender.city),
      } : dto.letterbox_sender,
      patient_portal: dto.patient_portal ? {
        ...dto.patient_portal,
        url: this.unescapeSpecialChars(dto.patient_portal.url),
      } : dto.patient_portal,
      bank_info: dto.bank_info ? {
        ...dto.bank_info,
        name: this.unescapeSpecialChars(dto.bank_info.name),
        iban: this.unescapeSpecialChars(dto.bank_info.iban),
        bic: this.unescapeSpecialChars(dto.bank_info.bic),
      } : dto.bank_info,
      footer: dto.footer ? dto.footer.map((row: string[]): string[] =>
        row.map((cell: string): string => this.unescapeSpecialChars(cell))) : dto.footer,
      autoarrange: dto.autoarrange ?? false,
      autocreate: dto.autocreate ?? false,
      contact_block1: this.contentBlockToModel(dto.contact_block1),
      contact_block2: this.contentBlockToModel(dto.contact_block2),
      header: this.headerToModel(dto.header),
      dunning: this.dunningToModel(dto.dunning),
    };
  }

  static toSpecificModel(standardConfigDto: ITemplateConfigurationDto, dto: ITemplateModificationDto)
    : ITemplateConfigurationFormValue {
    let override: ITemplateConfigurationDto = this.deepMerge(standardConfigDto, dto.__override);

    // remove dynamic fields from contact_blocks that don't appear in override
    if (override.contact_block1 && dto.__override?.contact_block1 &&
      Object.keys(override.contact_block1.content).length
      !== Object.keys(dto.__override.contact_block1.content).length) {
      override.contact_block1.content = dto.__override.contact_block1.content;
    }

    if (override.contact_block2 && dto.__override?.contact_block2 &&
      Object.keys(override.contact_block2.content).length
      !== Object.keys(dto.__override.contact_block2.content).length) {
      override.contact_block2.content = dto.__override.contact_block2.content;
    }

    return {
      ...override,
      period: dto.period,
      charge: dto.charge,
      defaultinterest: dto.defaultinterest,
      account: this.unescapeSpecialChars(dto.account),
      costlocation: this.unescapeSpecialChars(dto.costlocation),
      delay: dto.delay,
      autoarrange: dto.autoarrange ?? false,
      autocreate: dto.autocreate ?? false,
      iscustomdunning: dto.iscustomdunning,
      dunning: this.dunningToModel(dto.dunning),
      contact_block1: this.contentBlockToModel(override.contact_block1),
      contact_block2: this.contentBlockToModel(override.contact_block2),
      header: this.headerToModel(override.header),
      custom_options: Object.keys(dto).filter((key: string): boolean =>
        !['__override', 'period', 'charge', 'defaultinterest', 'account', 'costlocation',
          'delay', 'autoarrange', 'autocreate', 'iscustomdunning', 'dunning']
          .includes(key)
      ).map((key: string) => {
        return {
          key,
          value: this.unescapeSpecialChars(dto[key]),
        }
      })
    };
  }

  /**
   * Merge the config data into the standardConfig data recursively
   * @param standardConfig
   * @param config
   */
  static deepMerge(standardConfig: any, config: any): ITemplateConfigurationDto {
    for (const key in config) {
      // Check if config's attribute is an object and if standardConfig's attribute is also an object
      if (config[key] !== null && typeof config[key] === 'object' && !Array.isArray(config[key])) {
        if (!standardConfig[key]) {
          standardConfig[key] = {};
        }
        // Recursively merge objects
        this.deepMerge(standardConfig[key], config[key]);
      } else {
        // Overwrite standardConfig's attribute with config's attribute
        standardConfig[key] = config[key];
      }
    }
    return standardConfig;
  }

  static contentBlockToModel(contentBlock: TContactBlockDto): TContactBlockModel {
    return contentBlock ? {
      title: this.unescapeSpecialChars(contentBlock.title ?? ''),
      content: contentBlock.content ? Object.keys(contentBlock.content).map((key: string) => {
        return {
          label: this.unescapeSpecialChars(key),
          value: this.unescapeSpecialChars(contentBlock.content[key])
        };
      }) : [],
    } : null;
  }

  static headerToModel(header: THeaderDto): THeaderModel {
    return {
      ...header,
      title: this.unescapeSpecialChars(header?.title),
      subtitle: this.unescapeSpecialChars(header?.subtitle),
      logo: header?.logo ? {
        ...header.logo,
        data: {data: header.logo?.data ?? '', name: '', fileFormate: null},
        width: header.logo.width?.toString().replace(/\./g, ','),
      } : null,
    };
  }

  static toDefaultDto(model: ITemplateConfigurationFormValue): ITemplateConfigurationDto {
    return {
      ...model,
      contact_note: this.escapeSpecialChars(model.contact_note),
      letterbox_sender: model.letterbox_sender ? {
        ...model.letterbox_sender,
        name: this.escapeSpecialChars(model.letterbox_sender.name),
        street: this.escapeSpecialChars(model.letterbox_sender.street),
        street_number: this.escapeSpecialChars(model.letterbox_sender.street_number),
        postal_code: this.escapeSpecialChars(model.letterbox_sender.postal_code),
        city: this.escapeSpecialChars(model.letterbox_sender.city),
      } : model.letterbox_sender,
      patient_portal: model.patient_portal ? {
        ...model.patient_portal,
        url: this.escapeSpecialChars(model.patient_portal.url),
      } : model.patient_portal,
      bank_info: model.bank_info ? {
        ...model.bank_info,
        name: this.escapeSpecialChars(model.bank_info.name),
        iban: this.escapeSpecialChars(model.bank_info.iban),
        bic: this.escapeSpecialChars(model.bank_info.bic),
      } : model.bank_info,
      footer: model.footer ? model.footer.map((row: string[]): string[] =>
        row.map((cell: string): string => this.escapeSpecialChars(cell))) : model.footer,
      // An attribute the user cleared has to be sent as null so the backend overrides it. An
      // attribute that is not part of the override at all stays undefined, so it is left out of the
      // request and the backend keeps the standard value instead of nulling it.
      contact_block1: model.contact_block1 ? this.contentBlockToDto(model.contact_block1)
        : (model.contact_block1 === undefined ? undefined : null),
      contact_block2: model.contact_block2 ? this.contentBlockToDto(model.contact_block2)
        : (model.contact_block2 === undefined ? undefined : null),
      header: this.headerToDto(model.header),
    }
  }

  static toSpecificDto(model: ITemplateConfigurationFormValue, isInvoice: boolean): ITemplateModificationDto {
    const customOptions: { [p: string]: string } = (model.custom_options ?? [])
      .reduce((acc: { [p: string]: string }, currentValue: { key: string, value: string }): { [p: string]: string } => {
      acc[currentValue.key] = this.escapeSpecialChars(currentValue.value)
      return acc;
    }, {} as { [key: string]: string });
    const override: ITemplateConfigurationFormValue = {
      ...model,
      custom_options: undefined,
      period: undefined,
      charge: undefined,
      defaultinterest: undefined,
      account: undefined,
      costlocation: undefined,
      delay: undefined,
      autoarrange: undefined,
      autocreate: undefined,
      iscustomdunning: undefined,
      dunning: undefined,
    };
    return {
      ...customOptions,
      __override: this.toDefaultDto(override),
      period: isInvoice ? undefined : model.period,
      charge: isInvoice ? undefined : model.charge,
      defaultinterest: isInvoice ? undefined : model.defaultinterest,
      account: isInvoice ? undefined : this.escapeSpecialChars(model.account),
      costlocation: isInvoice ? undefined : this.escapeSpecialChars(model.costlocation),
      delay: isInvoice ? undefined : model.delay,
      autoarrange: isInvoice ? undefined : model.autoarrange,
      autocreate: isInvoice ? undefined : model.autocreate,
      iscustomdunning: isInvoice ? undefined : model.iscustomdunning,
      dunning: isInvoice ? undefined : this.dunningToDto(model.dunning),
    }
  }

  static contentBlockToDto(contactBlock: TContactBlockModel): TContactBlockDto {
    return {
      ...contactBlock,
      title: this.escapeSpecialChars(contactBlock.title),
      content: contactBlock.content ? contactBlock.content.reduce(
        (acc: { [p: string]: string }, current_value: { label: string, value: string }): { [p: string]: string } => {
        acc[this.escapeSpecialChars(current_value.label) ?? ''] = this.escapeSpecialChars(current_value.value)
        return acc;
      }, {} as { [key: string]: string }) : {},
    }
  }

  static headerToDto(header: THeaderModel): THeaderDto {
    if (!header) return header === undefined ? undefined : null;
    let width: string =
      header?.logo?.width?.replace(/ /g, '')
        .replace(/,/g, '.')
        .replace(/''/g, 'in')
        .replace(/″/g, 'in');
    if (width) {
      if (width.endsWith('dm')) {
        const value: number = parseFloat(width.substring(0, width.length - 2));
        width = (value * 10) + 'cm';
      }
    }

    return {
      ...header,
      title: this.escapeSpecialChars(header?.title),
      subtitle: this.escapeSpecialChars(header?.subtitle),
      logo: header?.logo ? {
        ...header?.logo,
        data: typeof header?.logo.data?.data == 'string' ?
          header?.logo.data?.data.substring(header?.logo.data?.data.indexOf(',') + 1) :
          (header?.logo.data?.data === undefined ? undefined : null),
        width: width,
      } : header?.logo === undefined ? undefined : null,
    };
  }

  static dunningToModel(dunning: ITemplateConfigurationDto['dunning']): ITemplateConfigurationFormValue['dunning'] {
    if (!dunning) return undefined;
    const first_block_text: string = Array.isArray(dunning.first_block_text) ?
      dunning.first_block_text.join('\n') : dunning.first_block_text;
    const second_block_text: string = Array.isArray(dunning.second_block_text) ?
      dunning.second_block_text.join('\n') : dunning.second_block_text;
    const outro: string = Array.isArray(dunning.outro) ? dunning.outro.join('\n') : dunning.outro;

    return {
      title: this.unescapeSpecialChars(dunning.title),
      second_block_title: this.unescapeSpecialChars(dunning.second_block_title),
      first_block_text: this.unescapeSpecialChars(first_block_text),
      second_block_text: this.unescapeSpecialChars(second_block_text),
      outro: this.unescapeSpecialChars(outro),
    };
  }

  static dunningToDto(dunning: ITemplateConfigurationFormValue['dunning']): ITemplateConfigurationDto['dunning'] {
    if (!dunning) return undefined;
    const first_block_text: string[] = typeof dunning.first_block_text === 'string' ?
      this.escapeSpecialChars(dunning.first_block_text).split('\n') : (dunning.first_block_text ?? []);
    const second_block_text: string[] = typeof dunning.second_block_text === 'string' ?
      this.escapeSpecialChars(dunning.second_block_text).split('\n') : (dunning.second_block_text ?? []);
    const outro: string[] = typeof dunning.outro === 'string' ?
      this.escapeSpecialChars(dunning.outro).split('\n') : (dunning.outro ?? []);
    return {
      title: this.escapeSpecialChars(dunning.title),
      second_block_title: this.escapeSpecialChars(dunning.second_block_title),
      first_block_text,
      second_block_text,
      outro,
    };
  }
}
